import type { EventData } from '@shared/types/events.types';
import type { BalanceCardItem } from '@shared/components/panels/components/domain-sections/BalanceCardsSection/types';
import type { PaymentDetail } from '@shared/components/panels/types/panel-common.types';
import type { BaseTimelineEvent } from '@shared/components/panels/components/domain-sections/Timeline/types';

// Events Data
export const PAYMENT_LINK_EVENTS_DATA: EventData[] = [
  {
    id: 'evt_pl_001',
    eventType: 'payment_link.created',
    description: 'Payment link pl_3Qrh2sLxdSnlxqAY14cC2hSm created for $250.00',
    timestamp: '12/14/24, 10:30:00 AM',
    requestId: 'req_link_abc123def456',
    status: 'succeeded'
  },
  {
    id: 'evt_pl_002',
    eventType: 'payment_link.shared',
    description: 'Payment link sent via email to john.doe@example.com',
    timestamp: '12/14/24, 10:45:00 AM',
    requestId: 'req_link_def456ghi789',
    status: 'succeeded'
  },
  {
    id: 'evt_pl_003',
    eventType: 'payment_link.viewed',
    description: 'Payment link accessed from IP 192.168.1.1 (New York, US)',
    timestamp: '12/14/24, 2:22:00 PM',
    requestId: 'req_link_ghi789jkl012',
    status: 'verified'
  },
  {
    id: 'evt_pl_004',
    eventType: 'payment_intent.created',
    description: 'Payment intent pi_3Qrh2sLxdSnlxqAY1sukBfZg initiated via link',
    timestamp: '12/14/24, 2:25:00 PM',
    requestId: 'req_link_jkl012mno345',
    status: 'processing'
  },
  {
    id: 'evt_pl_005',
    eventType: 'payment_link.payment_succeeded',
    description: 'Payment of $250.00 completed through payment link',
    timestamp: '12/14/24, 2:27:00 PM',
    requestId: 'req_link_mno345pqr678',
    status: 'succeeded'
  },
  {
    id: 'evt_pl_006',
    eventType: 'receipt.sent',
    description: 'Payment receipt emailed to john.doe@example.com',
    timestamp: '12/14/24, 2:28:00 PM',
    requestId: 'req_link_pqr678stu901',
    status: 'succeeded'
  },
  {
    id: 'evt_pl_007',
    eventType: 'payment_link.updated',
    description: 'Payment link amount updated from $250.00 to $275.00',
    timestamp: '12/13/24, 4:15:00 PM',
    requestId: 'req_link_stu901vwx234',
    status: 'succeeded'
  },
  {
    id: 'evt_pl_008',
    eventType: 'payment_link.payment_failed',
    description: 'Payment attempt failed - Card declined (insufficient funds)',
    timestamp: '12/13/24, 3:45:00 PM',
    requestId: 'req_link_vwx234yza567',
    status: 'failed'
  },
  {
    id: 'evt_pl_009',
    eventType: 'payment_link.reminder_sent',
    description: 'Automated reminder sent for unpaid link to customer',
    timestamp: '12/13/24, 9:00:00 AM',
    requestId: 'req_link_yza567bcd890',
    status: 'succeeded'
  },
  {
    id: 'evt_pl_010',
    eventType: 'payment_link.clicked',
    description: 'Payment link clicked from email client (Gmail)',
    timestamp: '12/12/24, 6:30:00 PM',
    requestId: 'req_link_bcd890efg123',
    status: 'verified'
  },
  {
    id: 'evt_pl_011',
    eventType: 'payment_link.qr_scanned',
    description: 'Payment link QR code scanned from mobile device',
    timestamp: '12/12/24, 5:45:00 PM',
    requestId: 'req_link_efg123hij456',
    status: 'approved'
  },
  {
    id: 'evt_pl_012',
    eventType: 'payment_link.expired',
    description: 'Payment link pl_3Qrh2sLxdSnlxqAY14cC2hSm expired after 7 days',
    timestamp: '12/21/24, 12:00:00 AM',
    requestId: 'req_link_hij456klm789',
    status: 'failed'
  },
  {
    id: 'evt_pl_013',
    eventType: 'webhook.delivered',
    description: 'Webhook notification sent for payment_link.payment_succeeded',
    timestamp: '12/14/24, 2:27:15 PM',
    requestId: 'req_link_klm789nop012',
    status: 'succeeded'
  },
  {
    id: 'evt_pl_014',
    eventType: 'payment_link.refund_initiated',
    description: 'Refund of $75.00 initiated for payment link transaction',
    timestamp: '12/12/24, 11:30:00 AM',
    requestId: 'req_link_nop012qrs345',
    status: 'processing'
  },
  {
    id: 'evt_pl_015',
    eventType: 'payment_link.analytics_updated',
    description: 'Link conversion rate updated: 87.3% (2043 of 2341 visits)',
    timestamp: '12/14/24, 11:59:59 PM',
    requestId: 'req_link_qrs345tuv678',
    status: 'pending'
  }
];

// Balance Data
export const PAYMENT_LINK_BALANCE_DATA: BalanceCardItem[] = [
  {
    id: 'conversion_rate',
    label: 'Conversion Rate',
    totalBalance: '87.3%',
    balances: [
      { currency: 'Visits', amount: 2341, formatted: '2,341 total' },
      { currency: 'Conversions', amount: 2043, formatted: '2,043 paid' },
      { currency: 'Rate', amount: 87.3, formatted: '87.3%' }
    ],
    status: 'conversion_rate',
    bgColor: 'bg-green-50',
    textColor: 'text-green-900'
  },
  {
    id: 'revenue_generated',
    label: 'Revenue Generated',
    totalBalance: '$24,567.89',
    balances: [
      { currency: 'Today', amount: 1240.50, formatted: '$1,240.50' },
      { currency: 'This Week', amount: 8735.20, formatted: '$8,735.20' },
      { currency: 'This Month', amount: 24567.89, formatted: '$24,567.89' }
    ],
    status: 'revenue_generated',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-900'
  },
  {
    id: 'link_performance',
    label: 'Link Performance',
    totalBalance: '98.9%',
    balances: [
      { currency: 'Uptime', amount: 98.9, formatted: '98.9%' },
      { currency: 'Load Time', amount: 0.6, formatted: '0.6s' },
      { currency: 'Errors', amount: 0.1, formatted: '0.1%' }
    ],
    status: 'link_performance',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-900'
  }
];

// Transaction Data
export type PaymentLinkTransactionData = {
  id: string;
  risk: number;
  transactionId: string;
  type: string;
  amount: string;
  paymentMethod: string;
  customer: string;
  date: string;
  linkName?: string;
  status?: string;
};

export const PAYMENT_LINK_TRANSACTIONS_DATA: PaymentLinkTransactionData[] = [
  {
    id: 'txn-pl-001',
    risk: 10,
    transactionId: 'TXN-2024-8342',
    type: 'Payment',
    amount: '$250.00',
    paymentMethod: 'Visa •••• 4242',
    customer: 'John Doe',
    date: 'Dec 14',
    linkName: 'Invoice #INV-001',
    status: 'succeeded'
  },
  {
    id: 'txn-pl-002',
    risk: 25,
    transactionId: 'TXN-2024-8343',
    type: 'Payment',
    amount: '$175.50',
    paymentMethod: 'Mastercard •••• 5555',
    customer: 'Sarah Johnson',
    date: 'Dec 14',
    linkName: 'Monthly Subscription',
    status: 'succeeded'
  },
  {
    id: 'txn-pl-003',
    risk: 15,
    transactionId: 'TXN-2024-8344',
    type: 'Payment',
    amount: '$500.00',
    paymentMethod: 'PayPal',
    customer: 'Tech Solutions Inc.',
    date: 'Dec 13',
    linkName: 'Consulting Fee',
    status: 'succeeded'
  },
  {
    id: 'txn-pl-004',
    risk: 45,
    transactionId: 'TXN-2024-8345',
    type: 'Payment',
    amount: '$89.99',
    paymentMethod: 'Amex •••• 1001',
    customer: 'Michael Chen',
    date: 'Dec 13',
    linkName: 'Product Order',
    status: 'failed'
  },
  {
    id: 'txn-pl-005',
    risk: 20,
    transactionId: 'TXN-2024-8346',
    type: 'Refund',
    amount: '-$75.00',
    paymentMethod: 'Visa •••• 9876',
    customer: 'Emily Wilson',
    date: 'Dec 12',
    linkName: 'Service Refund',
    status: 'refunded'
  },
  {
    id: 'txn-pl-006',
    risk: 30,
    transactionId: 'TXN-2024-8347',
    type: 'Payment',
    amount: '$1,250.00',
    paymentMethod: 'Bank Transfer',
    customer: 'Global Industries LLC',
    date: 'Dec 12',
    linkName: 'Enterprise License',
    status: 'processing'
  },
  {
    id: 'txn-pl-007',
    risk: 5,
    transactionId: 'TXN-2024-8348',
    type: 'Payment',
    amount: '$39.99',
    paymentMethod: 'Apple Pay',
    customer: 'Lisa Park',
    date: 'Dec 11',
    linkName: 'Monthly Plan',
    status: 'succeeded'
  },
  {
    id: 'txn-pl-008',
    risk: 35,
    transactionId: 'TXN-2024-8349',
    type: 'Payment',
    amount: '$750.00',
    paymentMethod: 'Discover •••• 3333',
    customer: 'Robert Taylor',
    date: 'Dec 11',
    linkName: 'Annual Renewal',
    status: 'succeeded'
  },
  {
    id: 'txn-pl-009',
    risk: 18,
    transactionId: 'TXN-2024-8350',
    type: 'Payment',
    amount: '$425.75',
    paymentMethod: 'Google Pay',
    customer: 'Amanda Brown',
    date: 'Dec 10',
    linkName: 'Custom Package',
    status: 'succeeded'
  },
  {
    id: 'txn-pl-010',
    risk: 50,
    transactionId: 'TXN-2024-8351',
    type: 'Payment',
    amount: '$2,100.00',
    paymentMethod: 'Visa •••• 7777',
    customer: 'StartupXYZ',
    date: 'Dec 10',
    linkName: 'Bulk Order',
    status: 'pending'
  }
];

// Profile Data
export const PAYMENT_LINK_PROFILE_DETAILS: PaymentDetail[] = [
  { label: 'Limited use', value: 'Single Use - 1 payment maximum' },
  { label: 'Allow promotion codes', value: 'Yes' },
  { label: 'Collect addresses', value: 'Billing address required' },
  { label: 'Collect phone numbers', value: 'Optional field enabled' },
  { label: 'Allow business customers to provide tax IDs', value: 'Enabled for B2B transactions' },
  { label: 'Save payment details for future use', value: 'Yes - Customer consent required' },
  { label: 'Collect tax automatically', value: 'Enabled (8.25% sales tax)' },
  { label: 'Collect terms of service agreement', value: 'Required acceptance before payment' },
  { label: 'Confirmation page', value: 'Custom branded success page with receipt' },
  { label: 'Deactivated link page', value: 'Standard "Link no longer available" message' },
  { label: 'Call to action button', value: 'Pay Now - $1,250.00 USD' },
  { label: 'Post-payment invoice', value: 'Automatic email invoice sent to customer' },
  { label: 'Created', value: 'December 1, 2024 by Jessica Martinez' },
  { label: 'Expires', value: 'December 31, 2024 at 11:59 PM EST' }
];

// Timeline Data
export function generatePaymentLinkTimelineEvents(): BaseTimelineEvent[] {
  return [
    {
      id: 'link-created',
      title: 'Payment link created',
      timestamp: '12/14/2024 10:30',
      details: [
        { action: 'Amount: $250.00', timestamp: '12/14/2024 10:30' },
        { action: 'Currency: USD', timestamp: '12/14/2024 10:30' },
        { action: 'Expires: 12/21/2024', timestamp: '12/14/2024 10:30' }
      ]
    },
    {
      id: 'link-shared',
      title: 'Link shared via email',
      timestamp: '12/14/2024 10:45',
      details: [
        { action: 'Sent to: john.doe@example.com', timestamp: '12/14/2024 10:45' },
        { action: 'Email template: Standard Invoice', timestamp: '12/14/2024 10:45' },
        { action: 'Delivery confirmed', timestamp: '12/14/2024 10:46' }
      ]
    },
    {
      id: 'first-visit',
      title: 'Customer viewed payment link',
      timestamp: '12/14/2024 14:22',
      details: [
        { action: 'IP Location: New York, US', timestamp: '12/14/2024 14:22' },
        { action: 'Device: Mobile (iOS)', timestamp: '12/14/2024 14:22' },
        { action: 'Time on page: 2m 34s', timestamp: '12/14/2024 14:24' }
      ]
    },
    {
      id: 'payment-attempted',
      title: 'Payment attempt initiated',
      timestamp: '12/14/2024 14:25',
      details: [
        { action: 'Payment method: Visa •••• 4242', timestamp: '12/14/2024 14:25' },
        { action: '3D Secure verification required', timestamp: '12/14/2024 14:25' },
        { action: 'Verification completed', timestamp: '12/14/2024 14:26' }
      ]
    },
    {
      id: 'payment-successful',
      title: 'Payment completed successfully',
      timestamp: '12/14/2024 14:27',
      details: [
        { action: 'Transaction ID: TXN-2024-8342', timestamp: '12/14/2024 14:27' },
        { action: 'Amount paid: $250.00', timestamp: '12/14/2024 14:27' },
        { action: 'Receipt sent to customer', timestamp: '12/14/2024 14:28' }
      ]
    },
    {
      id: 'link-expired',
      title: 'Payment link expired',
      timestamp: '12/21/2024 00:00',
      details: [
        { action: 'Link automatically deactivated', timestamp: '12/21/2024 00:00' },
        { action: 'Total payments received: 1', timestamp: '12/21/2024 00:00' },
        { action: 'Total amount collected: $250.00', timestamp: '12/21/2024 00:00' }
      ]
    }
  ];
}